import {create} from "zustand";
import {createJSONStorage, persist} from "zustand/middleware";
import {WebappCms} from "@lib/Cms/CmsModels";

export type BootStatus = "idle" | "loading" | "ready" | "error";

export type BootFail =
    | "NOT_AUTHENTICATED"
    | "MISSING_B2C_COUNTRY"
    | "COUNTRY_DISABLED"
    | "ME_USER_MISSING"
    | "COUNTRY_MISMATCH"
    | "TENANT_FAILED"
    | "CMS_FAILED"
    | "I18N_FAILED"
    | "UNKNOWN";

export type AppContextVars = {
    status: BootStatus;
    statusReason?: BootFail | null;

    b2cCountry?: string | null;
    meCountry?: string | null;

    tenantId?: string | null;
    projectId?: string | null;
    language?: string | null;
    projectTimezone?: string | null;
    clientTimezone?: string | null;
    catalogInitiativeId?: string | null;
    currencyName?: string | null;
    currencySymbol?: string | null;

    cms?: WebappCms | null;
    translations?: Record<string, string> | null;
}

export type AppContextActions = {
    setCtx: (data: Partial<AppContextState>) => void;
    reset: () => void;
}

export type AppContextState = AppContextVars & AppContextActions;

const initialAppState: AppContextVars = {
    status: "idle",
    statusReason: null,
    b2cCountry: null,
    meCountry: null,
    tenantId: null,
    projectId: null,
    language: null,
    projectTimezone: null,
    clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cms: null,
    translations: null,
}

export const useAppContextStore = create<AppContextState>()(
    persist(
        (set) => ({
            ...initialAppState,
            setCtx: (data) =>
                set((prev) => {
                    const next = { ...prev, ...data };
                    console.log("[setCtx]", data, "=> tenantId:", next.tenantId, "projectId:", next.projectId);
                    return next;
                }),

            reset: () =>
                set(initialAppState),
        }),
        {
            name: "app_ctx",
            storage: createJSONStorage(() => localStorage),
            partialize: (s) => ({
                b2cCountry: s.b2cCountry,
                meCountry: s.meCountry,
                tenantId: s.tenantId,
                projectId: s.projectId,
                language: s.language,
                projectTimezone: s.projectTimezone,
                clientTimezone: s.clientTimezone,
                catalogInitiativeId: s.catalogInitiativeId,
                currencyName: s.currencyName,
                currencySymbol: s.currencySymbol,
                cms: s.cms,
                translations: s.translations,
            }),
        }
    )
);

export const useCms = () => useAppContextStore((s) => s.cms);
export const useCmsSettings = () => useAppContextStore((s) => s.cms?.configuration.appSettings);
export const useCmsPages = () => useAppContextStore((s) => s.cms?.configuration.pages);
export const useCmsHeader = () => useAppContextStore((s) => s.cms?.configuration.structure?.header);
export const useCmsFooter = () => useAppContextStore((s) => s.cms?.configuration.structure?.footer);
export const useCmsMainNavigation = () => useAppContextStore((s) => s.cms?.configuration.structure?.mainNavigation);
export const useCmsUserNavigation = () => useAppContextStore((s) => s.cms?.configuration.structure?.userNavigation);
export const useCmsFooterNavigation = () => useAppContextStore((s) => s.cms?.configuration.structure?.footerNavigation);
export const useTranslations = () => useAppContextStore((s) => s.translations);
export const useTenantId = () => useAppContextStore((s) => s.tenantId);
export const useProjectId = () => useAppContextStore((s) => s.projectId);
export const useProjectLanguage = () => useAppContextStore((s) => s.language);
export const usePrivateBootStatus = () => useAppContextStore((s) => s.status);
